import { PrototypeApiError } from './apiTransport';
import { renderCommunityIdeaDetail } from './communityIdeaDetailRenderer';
import type { IdeaSummary } from './ideaDetailController';
import { closeModal, handoffModalForNavigation, openModal } from './navigationController';
import type { Root } from './prototypeTypes';
import { getRuntime } from './runtimeAccessor';
import { captureCommunityReturnState, type CommunityReturnState } from './communityMountState';

interface DialogRequest {
	generation: number;
	ideaId: string;
	controller: AbortController;
	returnState: CommunityReturnState | null;
}

const requests = new WeakMap<object, DialogRequest>();

function dialog(root: Root): HTMLElement | null { return root.querySelector<HTMLElement>('#communityIdeaDetailModal'); }
function host(root: Root): HTMLElement | null { return dialog(root)?.querySelector<HTMLElement>('[data-community-detail-host]') ?? null; }

const dialogTitleId = 'communityIdeaDetailDialogTitle';

function labelDialog(root: Root): void {
	const modal = dialog(root)?.querySelector<HTMLElement>('.modal');
	if (!modal) return;
	modal.setAttribute('aria-labelledby', dialogTitleId);
	modal.removeAttribute('aria-label');
}

function paintState(root: Root, title: string, message: string, ideaId?: string): void {
	const target = host(root);
	if (!target) return;
	target.innerHTML = `<section class="community-detail-state" role="status"><h2 id="${dialogTitleId}" class="community-detail-title">${title}</h2><p>${message}</p>${ideaId ? `<button type="button" class="btn btn-secondary" data-proto-click="retryCommunityIdeaDialog('${ideaId}')" aria-label="Retry loading community request">Retry</button>` : ''}</section>`;
	labelDialog(root);
}

/**
 * Reserve the reader's information architecture without inventing content.
 * The modal shell has a stable height; these deliberately blank slots avoid
 * a loading label or an oversized shimmering mock that would misrepresent a
 * short real request when the response arrives.
 */
function paintLoadingSlots(root: Root): void {
	const target = host(root);
	if (!target) return;
	target.innerHTML = `<article class="community-detail community-detail--loading" aria-busy="true">
		<h2 id="${dialogTitleId}" class="community-detail-title" aria-label="Community pick details"><span class="community-detail-loading-slot community-detail-loading-slot--title" data-community-detail-loading-slot aria-hidden="true"></span></h2>
		<p class="community-detail-subtitle"><span class="community-detail-loading-slot community-detail-loading-slot--subtitle" data-community-detail-loading-slot aria-hidden="true"></span></p>
		<div class="community-detail-status-row" aria-hidden="true"><span class="community-detail-loading-slot community-detail-loading-slot--status" data-community-detail-loading-slot></span><span class="community-detail-loading-slot community-detail-loading-slot--vote" data-community-detail-loading-slot></span></div>
		<section class="community-detail-section community-detail-strengthen" aria-hidden="true"><h3><span class="community-detail-loading-slot community-detail-loading-slot--heading" data-community-detail-loading-slot></span></h3><span class="community-detail-loading-slot community-detail-loading-slot--list" data-community-detail-loading-slot></span></section>
		<section class="community-detail-section community-detail-weaken" aria-hidden="true"><h3><span class="community-detail-loading-slot community-detail-loading-slot--heading" data-community-detail-loading-slot></span></h3><span class="community-detail-loading-slot community-detail-loading-slot--list-compact" data-community-detail-loading-slot></span></section>
		<section class="community-detail-section community-detail-links" aria-hidden="true"><h3><span class="community-detail-loading-slot community-detail-loading-slot--heading" data-community-detail-loading-slot></span></h3><span class="community-detail-loading-slot community-detail-loading-slot--resource" data-community-detail-loading-slot></span></section>
		<section class="community-detail-section community-detail-attachments" aria-hidden="true"><h3><span class="community-detail-loading-slot community-detail-loading-slot--heading" data-community-detail-loading-slot></span></h3><span class="community-detail-loading-slot community-detail-loading-slot--resource" data-community-detail-loading-slot></span></section>
	</article>`;
	labelDialog(root);
}

function isCurrent(root: Root, request: DialogRequest): boolean {
	return requests.get(root as object) === request && !request.controller.signal.aborted && dialog(root)?.classList.contains('active') === true;
}

function startRequest(root: Root, ideaId: string, returnState = requests.get(root as object)?.returnState ?? null): DialogRequest {
	const previous = requests.get(root as object);
	previous?.controller.abort();
	const request: DialogRequest = {
		generation: (previous?.generation ?? 0) + 1,
		ideaId,
		controller: new AbortController(),
		returnState,
	};
	requests.set(root as object, request);
	paintLoadingSlots(root);
	void loadCommunityIdeaDialog(root, request);
	return request;
}

/** Opens the existing reader modal without changing the Community route. */
export function openCommunityIdeaDialog(root: Root, ideaId: string, returnFocus?: HTMLElement | null): void {
	const returnState = returnFocus ? captureCommunityReturnState(root, returnFocus) : null;
	if (returnState) {
		const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
		window.history.replaceState({ ...currentState, communityReturnState: returnState }, '');
	}
	openModal(root, 'communityIdeaDetailModal', returnFocus);
	startRequest(root, ideaId, returnState);
}

async function loadCommunityIdeaDialog(root: Root, request: DialogRequest): Promise<void> {
	try {
		const api = getRuntime(root)?.api;
		if (!api?.getCommunityIdea) throw new Error('community_detail_api_unavailable');
		const response = await api.getCommunityIdea(request.ideaId, request.controller.signal);
		if (!isCurrent(root, request)) return;
		if (!response?.idea) {
			paintState(root, 'Idea not found', 'This community pick is unavailable.');
			return;
		}
		const target = host(root);
		if (target) {
			target.innerHTML = renderCommunityIdeaDetail(response.idea as IdeaSummary, { surface: 'dialog' });
			const title = target.querySelector<HTMLElement>('.community-detail-title');
			if (title) title.id = dialogTitleId;
			labelDialog(root);
		}
	} catch (error) {
		if (!isCurrent(root, request) || request.controller.signal.aborted) return;
		const message = error instanceof PrototypeApiError && error.status === 404 ? 'This community pick is unavailable.' : 'Please try again.';
		paintState(root, 'Could not load idea', message, request.ideaId);
	}
}

export function retryCommunityIdeaDialog(root: Root, ideaId: string): void {
	const modal = dialog(root);
	if (!modal?.classList.contains('active')) return;
	startRequest(root, ideaId);
}

/** Cancels the active read before the shared modal primitive restores focus. */
export function closeCommunityIdeaDialog(root: Root): void {
	requests.get(root as object)?.controller.abort();
	requests.delete(root as object);
	closeModal(root, 'communityIdeaDetailModal');
}

/**
 * Hands the dialog's View Topic action to the normal Topic controller only
 * after its modal-history entry has been consumed. This prevents a Topic
 * child entry from inheriting `modalOpen`, and leaves Browser Back on the
 * original Community return entry.
 */
export function handoffCommunityIdeaDialogTopic(root: Root, navigateToTopic: () => void): void {
	// Read before close(): the request map is cleared by the teardown.
	const returnState = requests.get(root as object)?.returnState ?? null;
	handoffModalForNavigation(root, 'communityIdeaDetailModal', () => closeCommunityIdeaDialog(root), () => {
		if (returnState) {
			const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
			window.history.replaceState({ ...currentState, communityReturnState: returnState }, '');
		}
		navigateToTopic();
	});
}
